import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowLeft, ArrowUpRight, Calendar, Clock } from "lucide-react";
import { api } from "../lib/api";
import { MetaVerifiedBadge } from "../components/MetaVerifiedBadge";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "InstaVibe Blog — Instagram automation insights, guides & news";
    api.get("/blog")
      .then(({ data }) => setPosts(data.posts || []))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
    return () => { document.title = "InstaVibe | Instagram DM Automation & Marketing Tools for Business"; };
  }, []);

  return (
    <div className="min-h-screen bg-bone text-white" data-testid="page-blog">
      <header className="fixed top-0 w-full z-50 backdrop-blur-2xl bg-bone/80 border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12 h-16 sm:h-20 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 sm:gap-3 min-w-0" data-testid="blog-logo">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl overflow-hidden bg-obsidian shrink-0 flex items-center justify-center">
              <img src="/instavibe-logo.jpg" alt="InstaVibe" className="w-full h-full object-cover scale-[1.35]" />
            </div>
            <div className="font-heading font-black text-xl sm:text-2xl tracking-tighter leading-none truncate">
              InstaVibe<span className="text-klein">.</span>
            </div>
          </Link>
          <Link to="/" className="btn-ghost text-xs sm:text-sm whitespace-nowrap" data-testid="blog-back-home">
            <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Back to home</span>
          </Link>
        </div>
      </header>

      <main className="pt-28 sm:pt-32 pb-20 px-4 sm:px-6 md:px-12 max-w-[1100px] mx-auto">
        <div className="overline mb-4">— Blog</div>
        <h1 className="font-heading font-black text-4xl sm:text-5xl md:text-6xl tracking-tighter leading-none mb-4" data-testid="blog-title">
          Instagram automation, <span className="gradient-text">unfiltered.</span>
        </h1>
        <p className="text-base sm:text-lg text-white/70 max-w-2xl mb-12 leading-relaxed">
          Guides, case studies, and unvarnished opinions on Instagram DM automation, comment-to-DM funnels, and growing a creator business in 2026.
        </p>

        <div className="grid gap-5 sm:gap-6" data-testid="blog-list">
          {loading && <div className="text-white/50 text-sm font-mono">Loading posts…</div>}
          {!loading && posts.length === 0 && (
            <div className="surface-card p-8 text-white/60 text-sm" data-testid="blog-empty">
              No posts yet — check back soon.
            </div>
          )}
          {posts.map((post) => (
            <Link
              key={post.slug}
              to={`/blog/${post.slug}`}
              className="surface-card p-6 sm:p-8 block hover:border-klein/50 transition-colors group"
              data-testid={`blog-post-card-${post.slug}`}
            >
              <div className="flex flex-wrap items-center gap-4 text-xs text-white/50 mb-4 font-mono">
                <span className="inline-flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {post.date}</span>
                <span className="inline-flex items-center gap-1.5"><Clock className="w-3 h-3" /> {post.read_minutes} min read</span>
              </div>
              <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tighter leading-tight mb-3 group-hover:text-klein transition-colors">
                {post.title}
              </h2>
              <p className="text-sm sm:text-base text-white/70 leading-relaxed mb-4">{post.description}</p>
              <span className="inline-flex items-center gap-1.5 text-sm text-cyan font-semibold">
                Read post <ArrowUpRight className="w-4 h-4" />
              </span>
            </Link>
          ))}
        </div>
      </main>

      <footer className="border-t border-white/10 py-8 sm:py-10 px-4 sm:px-6 md:px-12 max-w-[1400px] mx-auto flex flex-wrap gap-4 sm:gap-6 justify-between items-center text-xs sm:text-sm text-white/60">
        <div>© 2026 InstaVibe. Not affiliated with Meta.</div>
        <MetaVerifiedBadge variant="compact" />
        <div className="flex gap-4 sm:gap-6">
          <Link to="/privacy" className="hover:text-white">Privacy</Link>
          <Link to="/terms" className="hover:text-white">Terms</Link>
          <Link to="/contact" className="hover:text-white">Contact</Link>
        </div>
      </footer>
    </div>
  );
}
